<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Taxes'"
      [canGoBack]="true"
      [noBow]="true" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">
    @if (loaded) {
      <tidy-row class="vertical-align-center">
        <ion-icon name="home" [style.color]="propertyColor" style="font-size: 16px; margin-right: 4px;" />
        <tidy-text
          [body]="propertyName"
          [translate]="false" />
      </tidy-row>

      @if (loadFailed) {
        <tidy-alert>
          <tidy-row class="extra-bottom-padding">
            <tidy-text
              [body]="'Could not load the taxes for this property.'" />
          </tidy-row>
          <tidy-row>
            <tidy-button
              [text]="'Try Again'"
              [action]="boundRetry"
              class="primary" />
          </tidy-row>
        </tidy-alert>
      } @else {
        @if (hero) {
          <tidy-card
            class="tax-accent-card"
            [ngStyle]="{ '--tax-accent-color': toneColor[hero.tone] }">
            <tidy-row class="vertical-align-center">
              <span
                class="tax-chip"
                [ngStyle]="{ '--chip-fg': toneColor[hero.tone], '--chip-bg': toneBg[hero.tone] }">
                <span class="tax-chip__dot"></span>
                {{ hero.label | translate }}
              </span>
            </tidy-row>
            <tidy-row class="extra-top-padding">
              <tidy-text
                [title]="hero.title"
                [translate]="false" />
            </tidy-row>
            <tidy-row>
              <tidy-text
                [helper]="hero.subtitle"
                [translate]="false" />
            </tidy-row>
            <tidy-row class="vertical-align-center">
              <tidy-image
                [src]="'assets/svg/cash-outline.svg'"
                class="body-size" />
              <tidy-text
                [helper]="hero.amount"
                [translate]="false" />
            </tidy-row>
          </tidy-card>
        }

        <tidy-row class="extra-top-padding" [alignLastItemRight]="true">
          <tidy-text
            [header]="'Taxes & Filings'" />
          <tidy-small-button
            [text]="'Add Tax'"
            [image]="'assets/img/add-button-white.svg'"
            (buttonClick)="addTax()" />
        </tidy-row>
        @if (taxes.length === 0) {
          <tidy-alert>
            <tidy-row>
              <tidy-text
                [body]="emptyText" />
            </tidy-row>
            @if (reportNote) {
              <tidy-row>
                <tidy-text
                  [helper]="reportNote"
                  [translate]="false" />
              </tidy-row>
            }
          </tidy-alert>
        } @else if (reportNote) {
          <tidy-row>
            <tidy-text
              [helper]="reportNote"
              [translate]="false" />
          </tidy-row>
        }
        @for (tax of taxes; track tax.id) {
          <tidy-card>
            <tidy-card-button
              (action)="openTaxDetails(tax)"
              class="arrow-to-top">
              <tidy-row class="vertical-align-center" [alignLastItemRight]="true">
                <tidy-text
                  [body]="tax.name"
                  [translate]="false" />
                <span
                  class="tax-chip"
                  [ngStyle]="{ '--chip-fg': toneColor[tax.statusTone], '--chip-bg': toneBg[tax.statusTone] }">
                  <span class="tax-chip__dot"></span>
                  {{ tax.statusLabel | translate }}
                </span>
              </tidy-row>
              <tidy-row>
                <tidy-text
                  [helper]="tax.categoryLine"
                  [translate]="false" />
              </tidy-row>
              <tidy-row class="vertical-align-center">
                <tidy-image
                  [src]="'assets/svg/calendar-outline.svg'"
                  class="body-size" />
                <tidy-text
                  [helper]="tax.nextDueDisplay"
                  [class]="tax.nextDueOverdue ? 'red' : ''"
                  [translate]="false" />
              </tidy-row>
            </tidy-card-button>
          </tidy-card>
        }

        @if (suggestions.length > 0) {
          <tidy-row class="extra-top-padding vertical-align-center">
            <tidy-text
              [body]="'Suggested for this address'" />
            <span>&nbsp;</span>
            <span class="tax-chip">{{ suggestions.length }}</span>
          </tidy-row>
          @for (suggestion of suggestions; track suggestion.id) {
            <tidy-card
              class="tax-accent-card"
              [ngStyle]="{ '--tax-accent-color': toneColor['warn'] }">
              <tidy-row class="vertical-align-center" [alignLastItemRight]="true">
                <tidy-text
                  [body]="suggestion.name"
                  [translate]="false" />
                @if (suggestion.rateLabel) {
                  <span class="tax-chip">{{ suggestion.rateLabel }}</span>
                }
              </tidy-row>
              <tidy-row class="vertical-align-center">
                <tidy-image
                  [src]="'assets/svg/bulb-outline.svg'"
                  class="body-size" />
                <tidy-text
                  [helper]="suggestion.reason"
                  [translate]="false" />
              </tidy-row>
              <tidy-row class="extra-top-padding vertical-align-center">
                <tidy-small-button
                  [text]="'Add'"
                  [image]="'assets/img/add-button-white.svg'"
                  (buttonClick)="addSuggestedTax(suggestion)" />
                <span>&nbsp;&nbsp;</span>
                <tidy-text
                  [actionBody]="'Dismiss'"
                  (action)="dismissSuggestion(suggestion)"
                  class="red link" />
              </tidy-row>
            </tidy-card>
          }
        }

        @if (jurisdictions.length > 0) {
          <tidy-row class="extra-top-padding">
            <tidy-text
              [header]="'Jurisdictions'" />
          </tidy-row>
          @for (jurisdiction of jurisdictions; track jurisdiction.key) {
            <tidy-card>
              <tidy-card-button
                (action)="openJurisdictionDetails(jurisdiction)"
                class="arrow-to-top">
                <tidy-row>
                  <tidy-text
                    [body]="jurisdiction.name"
                    [translate]="false" />
                </tidy-row>
                @if (jurisdiction.agency) {
                  <tidy-row>
                    <tidy-text
                      [helper]="jurisdiction.agency"
                      [translate]="false" />
                  </tidy-row>
                }
              </tidy-card-button>
            </tidy-card>
          }
        }
      }
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
